import React from 'react';
import { FileText, FileSpreadsheet, Image, Download, Trash2, Eye, MoreVertical, FileX } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import { ROLES } from '../../../utils/rbac';
import { formatFileSize, formatRelativeTime } from '../../../utils/mockDocumentsData';
import './DocumentsList.css';

const getFileIcon = (type) => {
  const fileType = type?.toLowerCase() || '';
  if (fileType === 'pdf') return FileText;
  if (fileType === 'doc' || fileType === 'docx') return FileText;
  if (fileType === 'xls' || fileType === 'xlsx') return FileSpreadsheet;
  if (fileType === 'jpg' || fileType === 'jpeg' || fileType === 'png') return Image;
  return FileText;
};

const getFileIconColor = (type) => {
  const fileType = type?.toLowerCase() || '';
  if (fileType === 'pdf') return '#8A4A3A';
  if (fileType === 'doc' || fileType === 'docx') return '#4A5A3A';
  if (fileType === 'xls' || fileType === 'xlsx') return '#5A7A3A';
  if (fileType === 'jpg' || fileType === 'jpeg' || fileType === 'png') return '#4A5A3A';
  return '#6B6B6B';
};

export function DocumentsList({ documents = [], currentUser, onView, onDownload, onDelete, onReExtract }) {
  const { t, i18n } = useTranslation();
  const canDelete = currentUser?.role === ROLES.ADMIN || 
                    currentUser?.role === ROLES.SUPERADMIN;

  const handleAction = (action, document, e) => {
    e.stopPropagation();
    switch (action) {
      case 'view':
        onView?.(document);
        break;
      case 'download':
        onDownload?.(document);
        break;
      case 'delete':
        if (window.confirm(t('reports.workspace.documents.confirmDelete', { name: document.name }))) {
          onDelete?.(document);
        }
        break;
      case 're-extract':
        onReExtract?.(document);
        break;
      default:
        break;
    }
  };

  if (documents.length === 0) {
    return (
      <div className="documents-list__empty">
        <FileX size={48} className="documents-list__empty-icon" />
        <h3 className="documents-list__empty-title">{t('reports.workspace.documents.empty.title')}</h3>
        <p className="documents-list__empty-message">
          {t('reports.workspace.documents.empty.message')}
        </p>
      </div>
    );
  }

  return (
    <div className="documents-list">
      {documents.map((doc) => {
        const FileIcon = getFileIcon(doc.type);
        const iconColor = getFileIconColor(doc.type);

        return (
          <div key={doc.id} className="documents-list__item">
            <div className="documents-list__item-icon" style={{ backgroundColor: 'rgba(74, 90, 58, 0.08)' }}>
              <FileIcon size={24} style={{ color: iconColor }} />
            </div>
            
            <div className="documents-list__item-info">
              <div className="documents-list__item-name">{doc.name}</div>
              <div className="documents-list__item-meta">
                <span>{formatFileSize(doc.size)}</span>
                {doc.uploadedAt && (
                  <span>{formatRelativeTime(doc.uploadedAt, i18n.resolvedLanguage || i18n.language, t)}</span>
                )}
                {doc.uploadedBy && (
                  <span>{t('reports.workspace.documents.by', { user: doc.uploadedBy })}</span>
                )}
              </div>
            </div>

            <div className="documents-list__item-actions">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="documents-list__actions-btn">
                    <MoreVertical size={18} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={(e) => handleAction('view', doc, e)}>
                    <Eye size={16} />
                    <span>{t('common.view')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => handleAction('download', doc, e)}>
                    <Download size={16} />
                    <span>{t('common.download')}</span>
                  </DropdownMenuItem>
                  {canDelete && (
                    <DropdownMenuItem 
                      onClick={(e) => handleAction('delete', doc, e)}
                      className="text-destructive"
                    >
                      <Trash2 size={16} />
                      <span>{t('common.delete')}</span>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        );
      })}
    </div>
  );
}
